import { useState, useEffect, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { AxiosResponse } from 'axios'

import { axiosInstance } from '../../helpers'
import {
    ProspectList,
    Prospect,
    ProspectListSearchStatus,
    ProspectListDetailSummary,
    PaginatedProspectListResponse,
    ProspectListInput,
    BulkEnrichPreviewResponse,
    BulkEnrichField,
} from '../../models/Outreach/types'

/**
 * Server-side quick filter for prospect list detail retrieve.
 * Drives `has_*` and `in_campaign` query params; `campaign_id` is an optional separate constraint.
 */
export type ProspectListQuickFilter =
    | 'all'
    | 'email'
    | 'phone'
    | 'profile'
    | 'in_campaign'

export const useProspectList = () => {
    const queryClient = useQueryClient()

    const {
        data: prospectLists,
        isLoading: isLoadingProspectLists,
        error: errorProspectLists,
    } = useQuery<AxiosResponse<ProspectListDetailSummary[]>, Error>({
        queryKey: ['prospectList'],
        queryFn: () => axiosInstance.get(`/outreach/prospect-lists/`),
        enabled: true,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const createProspectList = useMutation({
        mutationFn: (prospectList: ProspectListInput) =>
            axiosInstance.post('/outreach/prospect-lists/', prospectList),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['prospectList'],
            })
        },
        onError: (error) => {
            toast.error('Failed to create prospect list')
        },
    })

    const updateProspectList = useMutation({
        mutationFn: ({
            id,
            updates,
        }: {
            id: string | number
            updates: ProspectListInput
        }) =>
            axiosInstance.patch<ProspectListDetailSummary>(
                `/outreach/prospect-lists/${id}/`,
                updates,
            ),
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({ queryKey: ['prospectList'] })
            // Also invalidate the detail query so polling starts immediately
            queryClient.invalidateQueries({
                queryKey: ['prospectList', variables.id.toString()],
            })
            const taskId = response.data.task_id
            if (taskId) {
                sessionStorage.setItem(
                    `prospect_search_task_${variables.id}`,
                    taskId,
                )
                toast.info('Search started. This may take 30-45 seconds...')
            } else {
                toast.success('Prospect list updated')
            }
        },
        onError: () => {
            toast.error('Failed to update prospect list')
        },
    })

    const uploadSpreadsheet = useMutation({
        mutationFn: async ({
            listId,
            file,
            headerRow,
            columnMapping,
        }: {
            listId: string | number
            file: File
            headerRow: number
            columnMapping: {
                name: number | null
                first_name_header: number | null
                last_name_header: number | null
                linkedinProfile: number | null
                email: number | null
                phone: number | null
            }
        }) => {
            // First, update the prospect list status to UPLOAD_IN_PROGRESS
            await axiosInstance.patch(`/outreach/prospect-lists/${listId}/`, {
                list_status: ProspectListSearchStatus.UPLOAD_IN_PROGRESS,
            })

            // Then upload the spreadsheet
            const formData = new FormData()
            formData.append('file', file)
            formData.append('header_row', headerRow.toString())
            formData.append('column_mapping', JSON.stringify(columnMapping))

            return axiosInstance.post(
                `/outreach/prospect-lists/${listId}/upload-spreadsheet/`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                },
            )
        },
        onSuccess: (response, variables) => {
            // Store task ID in sessionStorage so useProspectDetail can pick it up
            console.log(
                '[Upload] Upload mutation success response:',
                response.data,
            )
            const taskId = response.data.task_id
            console.log('[Upload] Extracted task_id:', taskId)
            if (taskId) {
                const storageKey = `prospect_upload_task_${variables.listId}`
                console.log('[Upload] Storing task ID in sessionStorage:', {
                    key: storageKey,
                    taskId,
                })
                sessionStorage.setItem(storageKey, taskId)
            } else {
                console.warn('[Upload] No task_id in response!', response.data)
            }
            queryClient.invalidateQueries({ queryKey: ['prospectList'] })
            toast.info('Upload started. This may take a few moments...')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to upload spreadsheet'
            toast.error(errorMessage)
        },
    })

    return {
        prospectList: prospectLists?.data,
        isLoadingProspectLists,
        errorProspectLists,
        createProspectList,
        updateProspectList,
        uploadSpreadsheet,
    }
}

// Helper function to invalidate all prospect list queries
// This ensures consistency across ProspectListHome and ProspectListDetail
const invalidateAllProspectListQueries = (
    queryClient: ReturnType<typeof useQueryClient>,
    listId?: string | number,
) => {
    // Invalidate the list query (used by ProspectListHome)
    queryClient.invalidateQueries({ queryKey: ['prospectList'] })
    // Invalidate the detail query if listId is provided (used by ProspectListDetail)
    if (listId) {
        queryClient.invalidateQueries({
            queryKey: ['prospectList', listId.toString()],
        })
    }
}

export const useBulkEnrichPreview = (
    listId: string | undefined,
    field: BulkEnrichField | undefined,
    enabled: boolean,
) => {
    return useQuery<BulkEnrichPreviewResponse, Error>({
        queryKey: ['bulkEnrichPreview', listId, field],
        queryFn: async () => {
            const { data } = await axiosInstance.get<BulkEnrichPreviewResponse>(
                `/outreach/prospect-lists/${listId}/bulk-enrich-preview/`,
                { params: { field } },
            )
            return data
        },
        enabled: Boolean(listId && field && enabled),
    })
}

export const useProspectDetail = (listId: string | undefined) => {
    const queryClient = useQueryClient()
    // Pagination state
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(25)
    const [searchQuery, setSearchQuery] = useState('')
    const [quickFilter, setQuickFilter] = useState<ProspectListQuickFilter>(
        'all',
    )
    const [campaignIdFilter, setCampaignIdFilterState] = useState<
        number | null
    >(null)

    const setCampaignIdFilter = useCallback((id: number | null) => {
        setCampaignIdFilterState(id)
        setPage(1)
    }, [])

    const applyQuickFilter = useCallback((filter: ProspectListQuickFilter) => {
        setQuickFilter(filter)
        setPage(1)
    }, [])

    // Track multiple enriching prospects with their task IDs
    const [enrichingProspects, setEnrichingProspects] = useState<
        Array<{
            id: string | number
            field: string
            taskId: string
        }>
    >([])
    // Track bulk enrichment task
    const [bulkEnrichmentTask, setBulkEnrichmentTask] = useState<{
        taskId: string
        field: 'email' | 'phone' | 'linkedinProfile'
    } | null>(null)
    // Track search prospects task
    const [searchTask, setSearchTask] = useState<{
        taskId: string
    } | null>(null)
    // Track upload spreadsheet task
    const [uploadTask, setUploadTask] = useState<{
        taskId: string
    } | null>(null)

    const {
        data: prospectListDetail,
        isLoading: isLoadingProspectDetail,
        error: errorProspectDetail,
    } = useQuery<AxiosResponse<PaginatedProspectListResponse>, Error>({
        queryKey: [
            'prospectList',
            listId,
            page,
            pageSize,
            searchQuery,
            quickFilter,
            campaignIdFilter,
        ],
        queryFn: () => {
            const params = new URLSearchParams()
            params.append('page', page.toString())
            params.append('page_size', pageSize.toString())
            if (searchQuery) {
                params.append('search', searchQuery)
            }
            // Narrow results + summary_stats (backend ProspectList retrieve filters)
            if (quickFilter === 'email') {
                params.append('has_email', 'true')
            } else if (quickFilter === 'phone') {
                params.append('has_phone', 'true')
            } else if (quickFilter === 'profile') {
                params.append('has_profile', 'true')
            } else if (quickFilter === 'in_campaign') {
                params.append('in_campaign', 'true')
            }
            if (campaignIdFilter != null) {
                params.append('campaign_id', String(campaignIdFilter))
            }
            return axiosInstance.get(
                `/outreach/prospect-lists/${listId}/?${params.toString()}`,
            )
        },
        enabled: !!listId, // Only run query if listId exists
        refetchOnMount: true,
        // Poll every 5 seconds when search is in progress
        // Note: Task-based polling (below) will handle completion more efficiently,
        // but this serves as a fallback for status updates
        refetchInterval: (query) => {
            const response = query?.data as
                | AxiosResponse<PaginatedProspectListResponse>
                | undefined
            if (!response?.data) return false
            const prospectList = response.data
            // Poll if status is SEARCH_IN_PROGRESS or UPLOAD_IN_PROGRESS
            if (
                prospectList.summary_stats.list_status ===
                    ProspectListSearchStatus.SEARCH_IN_PROGRESS ||
                prospectList.summary_stats.list_status ===
                    ProspectListSearchStatus.UPLOAD_IN_PROGRESS
            ) {
                console.log('Polling for prospect list', listId)
                return 5000 // 5 seconds
            }
            return false // Stop polling when not in progress
        },
    })

    const archiveProspectList = useMutation({
        mutationFn: (listId: string | number) =>
            axiosInstance.delete(`/outreach/prospect-lists/${listId}/`),
        onSuccess: (_, listId) => {
            // Optimistically remove the archived list from cache
            queryClient.setQueryData<
                AxiosResponse<ProspectListDetailSummary[]>
            >(['prospectList'], (oldData) => {
                if (!oldData) return oldData
                return {
                    ...oldData,
                    data: oldData.data.filter((list) => list.id !== listId),
                }
            })
            // Also invalidate to ensure we have the latest data
            queryClient.invalidateQueries({ queryKey: ['prospectList'] })
            toast.success('Prospect list archived')
        },
        onError: () => {
            toast.error('Failed to archive prospect list')
            // Refetch on error to ensure UI is in sync
            queryClient.invalidateQueries({ queryKey: ['prospectList'] })
        },
    })

    // Track previous status to show toast notifications on status change
    const previousStatusRef = useRef<ProspectListSearchStatus | undefined>(
        prospectListDetail?.data?.summary_stats.list_status,
    )

    useEffect(() => {
        const currentStatus =
            prospectListDetail?.data?.summary_stats.list_status
        const previousStatus = previousStatusRef.current

        // Show toast if status changed from SEARCH_IN_PROGRESS to completed/failed
        if (
            previousStatus === ProspectListSearchStatus.SEARCH_IN_PROGRESS &&
            currentStatus
        ) {
            if (currentStatus === ProspectListSearchStatus.SEARCH_COMPLETED) {
                toast.success('Prospect search completed successfully')
            } else if (
                currentStatus === ProspectListSearchStatus.SEARCH_FAILED
            ) {
                toast.error('Prospect search failed. Please try again.')
            }
        }

        // Show toast if status changed from UPLOAD_IN_PROGRESS to completed/failed
        if (
            previousStatus === ProspectListSearchStatus.UPLOAD_IN_PROGRESS &&
            currentStatus
        ) {
            if (currentStatus === ProspectListSearchStatus.UPLOAD_COMPLETED) {
                toast.success('Spreadsheet upload completed successfully')
            } else if (
                currentStatus === ProspectListSearchStatus.UPLOAD_FAILED
            ) {
                toast.error('Spreadsheet upload failed. Please try again.')
            }
        }

        // Update the ref with the current status
        if (currentStatus) {
            previousStatusRef.current = currentStatus
        }
    }, [prospectListDetail?.data?.summary_stats.list_status])

    // Poll all active enrichment tasks
    useEffect(() => {
        if (enrichingProspects.length === 0) return

        const pollTasks = async () => {
            // Create a snapshot of enrichingProspects to avoid issues with state updates during iteration
            const currentEnrichingProspects = [...enrichingProspects]

            for (const enriching of currentEnrichingProspects) {
                try {
                    const response = await axiosInstance.get(
                        `/outreach/prospects/enrich_status/`,
                        {
                            params: { task_id: enriching.taskId },
                        },
                    )

                    if (response.data.status === 'completed') {
                        // Refetch the active query (using current page, pageSize, searchQuery from closure)
                        await queryClient.refetchQueries({
                            queryKey: [
                                'prospectList',
                                listId,
                                page,
                                pageSize,
                                searchQuery,
                            ],
                        })

                        // Now remove from tracking after data has been refetched
                        setEnrichingProspects((prev) =>
                            prev.filter((p) => p.taskId !== enriching.taskId),
                        )
                    } else if (response.data.status === 'failed') {
                        // Handle failure
                        setEnrichingProspects((prev) =>
                            prev.filter((p) => p.taskId !== enriching.taskId),
                        )
                        // Invalidate queries to refresh UI
                        queryClient.invalidateQueries({
                            queryKey: ['prospectList', listId],
                        })
                        toast.error('Enrichment failed. Please try again.')
                    }
                } catch (error) {
                    console.error('Error polling enrichment status:', error)
                }
            }
        }

        const interval = setInterval(pollTasks, 10000)
        return () => clearInterval(interval)
    }, [enrichingProspects, queryClient, listId])

    // Poll bulk enrichment task
    useEffect(() => {
        if (!bulkEnrichmentTask || !listId) return

        const pollBulkTask = async () => {
            try {
                const response = await axiosInstance.get(
                    `/outreach/prospect-lists/${listId}/bulk-enrich-status/`,
                    {
                        params: { bulk_task_id: bulkEnrichmentTask.taskId },
                    },
                )

                if (response.data.status === 'completed') {
                    // Store field before clearing the task
                    const completedField = bulkEnrichmentTask.field
                    // Clear the bulk task
                    setBulkEnrichmentTask(null)

                    // Invalidate queries to refresh data (only for this listId)
                    queryClient.invalidateQueries({
                        queryKey: ['prospectList', listId],
                    })
                } else if (response.data.status === 'failed') {
                    // Clear the bulk task to prevent further polling
                    setBulkEnrichmentTask(null)
                    queryClient.invalidateQueries({
                        queryKey: ['prospectList', listId],
                    })
                    toast.error('Bulk enrichment failed. Please try again.')
                }
            } catch (error) {
                console.error('Error polling bulk enrichment status:', error)
            }
        }

        const interval = setInterval(pollBulkTask, 4000)
        return () => clearInterval(interval)
    }, [bulkEnrichmentTask, queryClient, listId])

    // Initialize search task from sessionStorage on mount
    useEffect(() => {
        if (!listId) return
        const storedTaskId = sessionStorage.getItem(
            `prospect_search_task_${listId}`,
        )
        if (storedTaskId) {
            setSearchTask({ taskId: storedTaskId })
        }
    }, [listId])

    // Initialize upload task from sessionStorage on mount
    useEffect(() => {
        if (!listId) return
        const storedTaskId = sessionStorage.getItem(
            `prospect_upload_task_${listId}`,
        )
        console.log('[Upload] Checking sessionStorage for task:', {
            listId,
            storedTaskId,
        })
        if (storedTaskId) {
            console.log(
                '[Upload] Setting upload task from sessionStorage:',
                storedTaskId,
            )
            setUploadTask({ taskId: storedTaskId })
        }
    }, [listId])

    // Poll search prospects task
    useEffect(() => {
        if (!searchTask || !listId) return

        const pollSearchTask = async () => {
            try {
                const response = await axiosInstance.get(
                    `/outreach/prospect-lists/${listId}/search-prospects-status/`,
                    {
                        params: { task_id: searchTask.taskId },
                    },
                )

                if (response.data.status === 'completed') {
                    // Clear the search task and sessionStorage
                    setSearchTask(null)
                    sessionStorage.removeItem(`prospect_search_task_${listId}`)

                    // Invalidate queries to refresh data (only for this listId)
                    queryClient.invalidateQueries({
                        queryKey: ['prospectList', listId],
                    })

                    toast.success('Prospect search completed successfully')
                } else if (response.data.status === 'failed') {
                    // Handle failure
                    setSearchTask(null)
                    sessionStorage.removeItem(`prospect_search_task_${listId}`)
                    queryClient.invalidateQueries({
                        queryKey: ['prospectList', listId],
                    })
                    toast.error('Prospect search failed. Please try again.')
                }
            } catch (error) {
                console.error('Error polling search status:', error)
            }
        }

        const interval = setInterval(pollSearchTask, 10000)
        return () => clearInterval(interval)
    }, [searchTask, queryClient, listId])

    // Poll upload spreadsheet task
    useEffect(() => {
        if (!uploadTask || !listId) return

        const pollUploadTask = async () => {
            try {
                const response = await axiosInstance.get(
                    `/outreach/prospect-lists/${listId}/upload-spreadsheet-status/`,
                    {
                        params: { upload_task_id: uploadTask.taskId },
                    },
                )

                // Handle both 'in_progress' and 'in_progess' (backend typo)
                // Note: We don't update cache for in_progress - the main query's refetchInterval handles status updates
                if (response.data.status === 'completed') {
                    // Clear the upload task and sessionStorage
                    setUploadTask(null)
                    sessionStorage.removeItem(`prospect_upload_task_${listId}`)

                    // Invalidate queries to refresh data (only for this listId)
                    queryClient.invalidateQueries({
                        queryKey: ['prospectList', listId],
                    })

                    toast.success('Spreadsheet upload completed successfully')
                } else if (response.data.status === 'failed') {
                    // Handle failure
                    setUploadTask(null)
                    sessionStorage.removeItem(`prospect_upload_task_${listId}`)
                    queryClient.invalidateQueries({
                        queryKey: ['prospectList', listId],
                    })
                    toast.error('Spreadsheet upload failed. Please try again.')
                }
            } catch (error) {
                console.error('[Upload] Error polling upload status:', error)
            }
        }

        // Poll immediately, then set interval
        pollUploadTask()
        const interval = setInterval(pollUploadTask, 4000)
        return () => clearInterval(interval)
    }, [uploadTask, queryClient, listId])

    const enrichProspectField = useMutation({
        mutationFn: ({
            prospectId,
            field,
            listId,
        }: {
            prospectId: string | number
            field: 'email' | 'phone' | 'linkedinProfile'
            listId?: string
        }) =>
            axiosInstance.post(`/outreach/prospects/${prospectId}/enrich/`, {
                field,
                ...(listId ? { listId } : {}),
            }),
        onSuccess: (response, variables) => {
            // Add this enrichment to the tracking array
            setEnrichingProspects((prev) => [
                ...prev,
                {
                    id: variables.prospectId,
                    field: variables.field,
                    taskId: response.data.task_id,
                },
            ])
            toast.info('Enrichment started...')
        },
        onError: (error) => {
            toast.error('Failed to start enrichment')
        },
    })

    const enrichProspectsBulk = useMutation({
        mutationFn: ({
            enrichmentType,
            field,
            listId,
        }: {
            enrichmentType: 'NEVER_ATTEMPTED' | 'MISSING' | 'ALL'
            field: 'email' | 'phone' | 'linkedinProfile'
            listId: string
        }) =>
            axiosInstance.post(
                `/outreach/prospect-lists/${listId}/bulk-enrich/`,
                {
                    enrichment_type: enrichmentType,
                    field,
                },
            ),
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({
                queryKey: ['bulkEnrichPreview', variables.listId],
            })
            // Track the bulk enrichment task
            setBulkEnrichmentTask({
                taskId: response.data.bulk_task_id,
                field: variables.field,
            })
            toast.info(`Bulk ${variables.field} enrichment started...`)
        },
        onError: (error) => {
            toast.error('Failed to start bulk enrichment')
        },
    })

    const addProspectToCampaign = useMutation({
        mutationFn: ({
            prospectId,
            campaignId,
        }: {
            prospectId: string | number
            campaignId: string | number
        }) =>
            axiosInstance.post(
                `/outreach/prospects/${prospectId}/add-to-campaign/`,
                {
                    campaign_id: campaignId,
                },
            ),
        onSuccess: () => {
            // Invalidate the prospect list detail query to refresh the data
            invalidateAllProspectListQueries(queryClient, listId)
            toast.success('Prospect added to campaign successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to add prospect to campaign'
            toast.error(errorMessage)
        },
    })

    const removeProspectFromCampaign = useMutation({
        mutationFn: ({
            prospectId,
            campaignId,
        }: {
            prospectId: string | number
            campaignId: string | number
        }) =>
            axiosInstance.post(
                `/outreach/prospects/${prospectId}/remove-from-campaign/`,
                { campaign_id: campaignId },
            ),
        onSuccess: () => {
            // Invalidate the prospect list detail query to refresh the data
            invalidateAllProspectListQueries(queryClient, listId)
            toast.success('Prospect removed from campaign successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to remove prospect from campaign'
            toast.error(errorMessage)
        },
    })

    const addProspectsToCampaignBulk = useMutation({
        mutationFn: ({
            prospectIds,
            campaignId,
        }: {
            prospectIds: (string | number)[]
            campaignId: string | number
        }) =>
            axiosInstance.post(`/outreach/prospects/bulk-add-to-campaign/`, {
                prospect_ids: prospectIds,
                campaign_id: campaignId,
            }),
        onSuccess: () => {
            // Invalidate the prospect list detail query to refresh the data
            invalidateAllProspectListQueries(queryClient, listId)
            toast.success('Prospects added to campaign successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to add prospects to campaign'
            toast.error(errorMessage)
        },
    })

    // Determine if search is in progress
    const isSearching =
        prospectListDetail?.data?.summary_stats.list_status ===
        ProspectListSearchStatus.SEARCH_IN_PROGRESS

    return {
        prospectListDetail: prospectListDetail?.data,
        isLoadingProspectDetail,
        errorProspectDetail,
        enrichProspectField,
        enrichProspectsBulk,
        isEnriching: enrichingProspects.length > 0, // Expose enrichment status for UI
        isBulkEnriching: bulkEnrichmentTask !== null, // Expose bulk enrichment status
        enrichingProspects, // Expose which prospects are being enriched
        isSearching, // Expose search status for UI
        archiveProspectList,
        addProspectToCampaign,
        addProspectsToCampaignBulk,
        removeProspectFromCampaign,
        // Pagination controls
        page,
        pageSize,
        searchQuery,
        setPage,
        setPageSize,
        setSearchQuery,
        quickFilter,
        setQuickFilter,
        applyQuickFilter,
        campaignIdFilter,
        setCampaignIdFilter,
        count: prospectListDetail?.data?.count || 0,
        hasNextPage: !!prospectListDetail?.data?.next,
        hasPreviousPage: !!prospectListDetail?.data?.previous,
    }
}
